import React from "react";
import { Navigation } from "./navigation";
import { Footer } from "./footer";
import { agentProducts, specialistApplications, agentScenarios } from "../data/agent-products";
import "./agent-products.css";

const ExternalLink = ({ href, children, className = "" }) => (
  <a href={href} className={className} target="_blank" rel="noopener noreferrer">
    {children} <i className="fa fa-external-link" aria-hidden="true" />
  </a>
);

export const AgentPlatformPage = ({ onHome, contact }) => {
  const scrollTo = (event, id) => {
    event.preventDefault();
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth", block: "start" });
  };
  const contactTeam = (event) => {
    event.preventDefault();
    onHome("contact");
  };

  return (
    <div className="agent-page agent-product-page">
      <Navigation detailMode onHome={onHome} />
      <main>
        <header className="ap-intro">
          <div className="container">
            <span className="ap-eyebrow">GOLDLY AI / SINGAPORE</span>
            <h1>AI Agents &amp; Applications</h1>
            <p>
              Meet Shiye Agent and Super Agent: our platforms for business workflows
              and specialist collaboration. Explore the products, their workspaces,
              and focused applications for sports analysis and education.
            </p>
            <div className="ap-intro-actions">
              <a href="#shiye-agent" data-scroll-ignore className="ap-button" onClick={(event) => scrollTo(event, "shiye-agent")}>Explore the products</a>
              <a href="#contact" data-scroll-ignore className="ap-link" onClick={contactTeam}>Discuss your use case <i className="fa fa-arrow-right" aria-hidden="true" /></a>
            </div>
          </div>
        </header>

        <nav className="ap-page-index" aria-label="On this page">
          <div className="container">
            {[
              ["shiye-agent", "Shiye Agent"],
              ["super-agent", "Super Agent"],
              ["specialist-applications", "Industry applications"],
              ["agent-use-cases", "Use cases"],
            ].map(([id, name]) => (
              <a href={"#" + id} data-scroll-ignore key={id} onClick={(event) => scrollTo(event, id)}>{name}</a>
            ))}
          </div>
        </nav>

        {agentProducts.map((product, index) => (
          <section className="ap-product" id={product.id} key={product.id} aria-labelledby={product.id + "-title"}>
            <div className="container">
              <div className="ap-product-heading">
                <div>
                  <span className="ap-eyebrow">{product.category}</span>
                  <h2 id={product.id + "-title"}>{product.name}</h2>
                </div>
                <ExternalLink href={product.url} className="ap-button">Visit {product.name}</ExternalLink>
              </div>
              <p className="ap-product-summary">{product.summary}</p>
              <div className="ap-product-layout">
                <figure className="ap-product-figure">
                  <a href={product.image} target="_blank" rel="noopener noreferrer" aria-label={"Open full-size " + product.name + " screenshot"}>
                    <img
                      src={product.image}
                      alt={product.name + " product interface"}
                      width="1536"
                      height="1024"
                      loading={index === 0 ? "eager" : "lazy"}
                      decoding="async"
                    />
                  </a>
                  <figcaption>
                    <span>{product.name} / Product overview</span>
                    <ExternalLink href={product.image}>View full-size</ExternalLink>
                  </figcaption>
                </figure>
                <div className="ap-product-description">
                  <p className="ap-audience"><strong>For your team</strong>{product.audience}</p>
                  <dl className="ap-feature-list">
                    {product.features.map(feature => (
                      <div key={feature.title}>
                        <dt>{feature.title}</dt>
                        <dd>{feature.text}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              </div>
              <div className="ap-example">
                <span>Example brief</span>
                <p>{product.example}</p>
              </div>
            </div>
          </section>
        ))}

        <section className="ap-applications" id="specialist-applications">
          <div className="container">
            <div className="ap-section-heading">
              <span className="ap-eyebrow">FOCUSED APPLICATIONS</span>
              <h2>AI for a specific task.</h2>
              <p>Alongside our agent platforms, explore applications built around a defined subject and a clear output.</p>
            </div>
            <div className="ap-application-grid">
              {specialistApplications.map(app => (
                <article className="ap-application" key={app.id}>
                  <a href={app.image} target="_blank" rel="noopener noreferrer" aria-label={"Open full-size " + app.name + " screenshot"} className="ap-application-image">
                    <img src={app.image} alt={app.name + " product interface"} loading="lazy" decoding="async" />
                  </a>
                  <div className="ap-application-copy">
                    <span className="ap-eyebrow">{app.category}</span>
                    <h3>{app.name}</h3>
                    <p>{app.summary}</p>
                    <p className="ap-output"><strong>What you get</strong>{app.output}</p>
                    <p className="ap-application-note">{app.note}</p>
                    {app.url
                      ? <ExternalLink href={app.url} className="ap-link">Visit {app.name}</ExternalLink>
                      : <a href="#contact" data-scroll-ignore className="ap-link" onClick={contactTeam}>Enquire about this application <i className="fa fa-arrow-right" aria-hidden="true" /></a>}
                  </div>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ap-use-cases" id="agent-use-cases">
          <div className="container">
            <div className="ap-section-heading">
              <span className="ap-eyebrow">FIND YOUR STARTING POINT</span>
              <h2>Start with the work you want to improve.</h2>
              <p>These example briefs connect common team tasks with the relevant platform. Discuss your data, systems and review requirements with us.</p>
            </div>
            <div className="ap-scenario-grid">
              {agentScenarios.map(scenario => (
                <article key={scenario.name}>
                  <h3>{scenario.name}</h3>
                  <p>{scenario.task}</p>
                  <a href={"#" + scenario.target} data-scroll-ignore className="ap-link" onClick={(event) => scrollTo(event, scenario.target)}>
                    Explore {scenario.product} <i className="fa fa-arrow-right" aria-hidden="true" />
                  </a>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="ap-foundation">
          <div className="container">
            <div className="ap-foundation-grid">
              <div>
                <span className="ap-eyebrow">SUPPORTING YOUR DEPLOYMENT</span>
                <h2>Model access and cloud delivery.</h2>
                <p>Goldly AI also provides LLM gateways and multi-cloud services to support your application's model and infrastructure requirements.</p>
              </div>
              <div>
                <h3>Our model gateway products</h3>
                <ul>
                  <li><ExternalLink href="https://tokenshengsheng.com">Shengsheng Token</ExternalLink></li>
                  <li><ExternalLink href="https://tokenwenwen.com">Wenwen Token</ExternalLink></li>
                  <li><ExternalLink href="https://pandasrouter.com">PandasRouter</ExternalLink></li>
                </ul>
              </div>
            </div>
            <div className="ap-contact">
              <div><h2>Which product fits your team?</h2><p>Tell our Singapore-based team about your task, current systems and the output you need.</p></div>
              <a href="#contact" data-scroll-ignore className="ap-button" onClick={contactTeam}>Talk to our team</a>
            </div>
          </div>
        </section>
      </main>
      <Footer contact={contact} onHome={onHome} />
    </div>
  );
};
